// DonutLabelingSettings.jsx — Task 3.1
//
// Settings panel for the Donut labeling screen.
// Persisted to localStorage under key "donut_labeling_settings".
//
// Props:
//   settings : object           — current settings (DEFAULT_SETTINGS shape)
//   onChange : (newSettings) => void
//   onClose  : () => void

import React from "react";

export const DEFAULT_SETTINGS = {
  models: { donut: true, gemini: true }, // which AI models to call
  auto_extract: true, // auto-call /extract on sample load
  highlight: {
    iso: true, // red border on ISO validation failure
    ensemble_diff: true, // orange row when Donut and Gemini disagree
    ai_low_conf: false, // placeholder — confidence threshold (future)
  },
  layout: {
    image_zoom_step: 1.2, // already wired in ImageViewer
  },
};

// ── localStorage helpers ────────────────────────────────────────────────────

export function loadSettings() {
  try {
    const raw = localStorage.getItem("donut_labeling_settings");
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    // Shallow-merge top level so new keys added to DEFAULT_SETTINGS survive
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      models: { ...DEFAULT_SETTINGS.models, ...(parsed.models || {}) },
      highlight: { ...DEFAULT_SETTINGS.highlight, ...(parsed.highlight || {}) },
      layout: { ...DEFAULT_SETTINGS.layout, ...(parsed.layout || {}) },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(s) {
  try {
    localStorage.setItem("donut_labeling_settings", JSON.stringify(s));
  } catch {}
}

// ── Panel component ─────────────────────────────────────────────────────────

export default function DonutLabelingSettings({ settings, onChange, onClose }) {
  const set = (path, value) => {
    // path: e.g. ["models", "donut"] or ["auto_extract"]
    const next = { ...settings };
    if (path.length === 1) {
      next[path[0]] = value;
    } else if (path.length === 2) {
      next[path[0]] = { ...next[path[0]], [path[1]]: value };
    }
    onChange(next);
    saveSettings(next);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 360,
        height: "100%",
        background: "#fff",
        borderLeft: "1px solid #e5e7eb",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.08)",
        zIndex: 1100,
        display: "flex",
        flexDirection: "column",
        fontFamily: "inherit",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #e5e7eb",
          background: "#f9fafb",
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13, color: "#111" }}>
          라벨링 설정
        </span>
        <button
          onClick={onClose}
          title="닫기"
          style={{
            marginLeft: "auto",
            fontSize: 12,
            padding: "3px 12px",
            borderRadius: 4,
            border: "1px solid #d1d5db",
            background: "#fff",
            cursor: "pointer",
            color: "#374151",
          }}
        >
          닫기
        </button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        {/* Section: AI 모델 */}
        <Section title="AI 모델">
          <CheckRow
            label="Donut v9"
            checked={settings.models.donut}
            onChange={(v) => set(["models", "donut"], v)}
            hint="Donut 모델 호출 (fast OCR)"
          />
          <CheckRow
            label="Gemini Flash 3"
            checked={settings.models.gemini}
            onChange={(v) => set(["models", "gemini"], v)}
            hint="Gemini 모델 호출 (LLM 교차 검증)"
          />
          {!settings.models.donut && !settings.models.gemini && (
            <p style={{ fontSize: 11, color: "#dc2626", margin: "6px 0 0" }}>
              모델이 하나도 선택되지 않으면 AI 추출이 동작하지 않습니다.
            </p>
          )}
        </Section>

        {/* Section: 자동 추출 */}
        <Section title="자동 추출">
          <CheckRow
            label="라벨링 진입 시 자동 AI 호출"
            checked={settings.auto_extract}
            onChange={(v) => set(["auto_extract"], v)}
            hint="끄면 'AI 다시 추출' 버튼을 직접 눌러야 합니다"
          />
        </Section>

        {/* Section: 하이라이트 */}
        <Section title="하이라이트">
          <CheckRow
            label="ISO 검증 실패 — 빨간 테두리"
            checked={settings.highlight.iso}
            onChange={(v) => set(["highlight", "iso"], v)}
            hint="VIN / 차량번호 형식 오류 시 입력란 강조"
          />
          <CheckRow
            label="Donut↔Gemini 불일치 — 주황 행"
            checked={settings.highlight.ensemble_diff}
            onChange={(v) => set(["highlight", "ensemble_diff"], v)}
            hint="두 모델이 다른 값을 추출한 필드 행 배경 강조"
          />
          <CheckRow
            label="AI confidence 낮음 (준비 중)"
            checked={settings.highlight.ai_low_conf}
            onChange={(v) => set(["highlight", "ai_low_conf"], v)}
            disabled
            hint="향후 confidence threshold 기반 강조 예정"
          />
        </Section>

        {/* Section: 초기화 */}
        <Section title="초기화">
          <button
            onClick={() => {
              onChange(DEFAULT_SETTINGS);
              saveSettings(DEFAULT_SETTINGS);
            }}
            style={{
              fontSize: 12,
              padding: "5px 14px",
              borderRadius: 4,
              border: "1px solid #d1d5db",
              background: "#fff",
              cursor: "pointer",
              color: "#374151",
            }}
          >
            기본값으로 초기화
          </button>
        </Section>
      </div>
    </div>
  );
}

// ── Internal helpers ─────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 8,
          paddingBottom: 4,
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

function CheckRow({ label, checked, onChange, hint, disabled }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        style={{ marginTop: 2, cursor: disabled ? "not-allowed" : "pointer" }}
      />
      <span>
        <span style={{ fontSize: 12, color: "#111827" }}>{label}</span>
        {hint && (
          <span
            style={{
              display: "block",
              fontSize: 11,
              color: "#9ca3af",
              marginTop: 1,
            }}
          >
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}
